import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { FaHome, FaImage, FaImages } from "react-icons/fa";

import { getMediaUrl } from "../utils/parametresUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10";

function ImagePreview({ url, label }) {
  const imageUrl = getMediaUrl(url);

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={label}
          className="h-56 w-full object-cover"
        />
      ) : (
        <div className="flex h-56 flex-col items-center justify-center text-slate-400">
          <FaImage className="text-4xl" />
          <p className="mt-3 text-sm font-medium">Aucune image</p>
        </div>
      )}
    </div>
  );
}

export default function HomeSettingsPanel({
  settings,
  galleries,
  loading,
  onChange,
  onQuillChange,
}) {
  return (
    <section className="space-y-6">
      <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        <div>
          <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300">
            <FaHome />
            Page d’accueil
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
            Header, section à propos et carrousel
          </h2>

          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Personnalisez les premiers éléments visibles sur la page d’accueil
            du site visiteur.
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
          <h3 className="text-lg font-bold text-slate-950 dark:text-white">
            Images du header
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Vous pouvez renseigner des URLs externes ou des chemins `/uploads`.
          </p>

          <div className="mt-5 grid gap-4">
            <div>
              <ImagePreview
                url={settings.header1_photo_url}
                label="Header photo 1"
              />

              <label className="mt-3 mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Lien photo header 1
              </label>
              <input
                type="text"
                name="header1_photo_url"
                value={settings.header1_photo_url || ""}
                onChange={onChange}
                placeholder="/uploads/... ou https://..."
                className={inputClass}
              />
            </div>

            <div>
              <ImagePreview
                url={settings.header2_photo_url}
                label="Header photo 2"
              />

              <label className="mt-3 mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Lien photo header 2
              </label>
              <input
                type="text"
                name="header2_photo_url"
                value={settings.header2_photo_url || ""}
                onChange={onChange}
                placeholder="/uploads/... ou https://..."
                className={inputClass}
              />
            </div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
          <h3 className="text-lg font-bold text-slate-950 dark:text-white">
            Textes d’accueil
          </h3>

          <div className="mt-5 space-y-5">
            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Titre du header
              </label>

              <div className="rounded-2xl bg-white dark:bg-slate-950">
                <ReactQuill
                  value={settings.header_title || ""}
                  onChange={(value) => onQuillChange("header_title", value)}
                  className="rounded-2xl bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
                  theme="snow"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Sous-titre du header
              </label>
              <input
                type="text"
                name="header_subtitle"
                value={settings.header_subtitle || ""}
                onChange={onChange}
                placeholder="Sous-titre de la page d’accueil"
                className={inputClass}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Titre de la section À propos
              </label>
              <input
                type="text"
                name="about_title"
                value={settings.about_title || ""}
                onChange={onChange}
                placeholder="Ex : Bienvenue au club"
                className={inputClass}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Contenu de la section À propos
              </label>

              <div className="rounded-2xl bg-white dark:bg-slate-950">
                <ReactQuill
                  value={settings.about_content || ""}
                  onChange={(value) => onQuillChange("about_content", value)}
                  className="min-h-[240px] rounded-2xl bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
                  theme="snow"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
        <div className="flex items-start gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300">
            <FaImages />
          </span>

          <div>
            <h3 className="text-lg font-bold text-slate-950 dark:text-white">
              Carrousel de la page d’accueil
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Sélectionnez une galerie photo utilisée pour alimenter le
              carrousel.
            </p>
          </div>
        </div>

        <div className="mt-5">
          <label className="mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200">
            Galerie du carrousel
          </label>

          <select
            name="carrousel_id"
            value={settings.carrousel_id || ""}
            onChange={onChange}
            disabled={loading}
            className={inputClass}
          >
            <option value="">Sélectionnez une galerie</option>
            {galleries.map((gallery) => (
              <option key={gallery.id} value={gallery.id}>
                {gallery.titre}
              </option>
            ))}
          </select>
        </div>
      </section>
    </section>
  );
}